import { faFloppyDisk, faPlus, faTrashAlt, faEdit, faXmark, IconDefinition, faGripLines, faArrowRight, faArrowLeft } from '@fortawesome/pro-solid-svg-icons'
import { faGlobe } from '@fortawesome/pro-light-svg-icons'
import { AnimatePresence, motion } from 'framer-motion'
import React, { useContext, useEffect, useState } from 'react'
import { usePutSocialsMutation } from '../../../../Redux/Api/Organisations';
import { all, max, min } from '../../../../Utils/InValidation'
import { putSocialsLocal } from '../../../../Redux/State/Organisation'
import { useAppDispatch, useAppSelector } from '../../../../Redux/store'
import BusyArea from '../../../Shared/BusyArea'
import Card from '../../../Shared/Card/Card'
import Button from '../../../Shared/Form/Button'
import Dropdown from '../../../Shared/Form/Dropdown'
import Form from '../../../Shared/Form/Form'
import Input, { FailedRequirements } from '../../../Shared/Form/Input'
import Title from '../../../Shared/Title'
import LoadingPage from '../../LoadingPage'
import { Hydra } from '../../../../Utils/Hydra'
import StatusPopups from '../../../Shared/Form/StatusPopups'
import { ModalContext } from "../../../Shared/Modal/Modal";
import { useTranslation } from "react-i18next";
import MobileApp from "../../../Shared/App/MobileApp";
import Socials from "../../../Shared/App/Socials";
import PageMaxWidth from '../../../Shared/pageRenderers/PageMaxWidth';
import MobilePreviewWrapper from '../../../Shared/pageRenderers/MobilePreviewWrapper';
import { faFacebook, faXTwitter, faInstagram, faYoutube, faLinkedin, faTiktok, faWhatsapp, faReddit, faPinterest, faSpotify } from '@fortawesome/free-brands-svg-icons';
import ScrollGradientContainer from '../../../Shared/ScrollGradientContainer';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import GenericModalShell from '../../../Shared/Modal/GenericModalShell';
import { useMediaQuery } from '@mui/material';

interface Social {
    text: string,
    url: string,
    type: string
}

const SocialsPage = () => {

    const org_socials = useAppSelector((s) => s.organisation.settings?.socials)
    const org_id = useAppSelector((s) => s.organisation.id)!
    const { t } = useTranslation()

    const dispatch = useAppDispatch()
    const modalContext = useContext(ModalContext)

    const [putSocials] = usePutSocialsMutation()

    const [busy, setBusy] = useState(false)
    const [socials, setSocials] = useState<Array<Social>>([])
    const [error, setError] = useState('');
    const [successMessage, setSuccessMessage] = useState('')

    const [customButtonText, setCustomButtonText] = useState<string[]>([]);
    const [customButtonUrl, setCustomButtonUrl] = useState<string[]>([]);
    const [customButtonIcon, setCustomButtonIcon] = useState<string[]>([]);

    const [editingSocial, setEditingSocial] = useState<Social | null>(null);
    const [editingIndex, setEditingIndex] = useState<number | null>(null);
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);

    const [isAddModalOpen, setIsAddModalOpen] = useState(false);
    const [newSocial, setNewSocial] = useState<Social>({ text: '', url: '', type: 'other' });

    const [shouldSubmit, setShouldSubmit] = useState(false);
    const [wasReordered, setWasReordered] = useState(false);

    const [draggedItem, setDraggedItem] = useState<number | null>(null);
    const [dropTargetIndex, setDropTargetIndex] = useState<number | null>(null);

    const isMobile = useMediaQuery('(max-width: 700px)')

    const inputEditModalRef = React.useRef<HTMLInputElement>(null);
    const inputAddModalRef = React.useRef<HTMLInputElement>(null);

    useEffect(() => {
        if (shouldSubmit) {
            setShouldSubmit(false);
            submit();
        }
    }, [socials]);

    const option = [
        { value: 'facebook', label: 'Facebook' },
        { value: 'twitter', label: 'X' },
        { value: 'instagram', label: 'Instagram' },
        { value: 'linkedin', label: 'LinkedIn' },
        { value: 'tiktok', label: 'TikTok' },
        { value: 'youtube', label: 'YouTube' },
        { value: 'whatsapp', label: 'WhatsApp' },
        { value: 'pinterest', label: 'Pinterest' },
        { value: 'reddit', label: 'Reddit' },
        { value: 'spotify', label: 'Spotify' },
        { value: 'other', label: t('organisation:socials:other') as string }
    ]

    const optionsFontAwesome = {
        facebook: faFacebook as IconDefinition,
        twitter: faXTwitter as IconDefinition,
        instagram: faInstagram as IconDefinition,
        linkedin: faLinkedin as IconDefinition,
        tiktok: faTiktok as IconDefinition,
        youtube: faYoutube as IconDefinition,
        whatsapp: faWhatsapp as IconDefinition,
        pinterest: faPinterest as IconDefinition,
        reddit: faReddit as IconDefinition,
        spotify: faSpotify as IconDefinition,
        other: faGlobe as IconDefinition
    };

    useEffect(() => {
        if (!org_socials) return
        setSocials(org_socials as Social[])

        if (org_socials.length > 0) {
            setCustomButtonText(org_socials.map((s) => s.text))
            setCustomButtonUrl(org_socials.map((s) => s.url))
            setCustomButtonIcon(org_socials.map((s) => s.type))
        }
    }, [org_socials])

    const submit = async () => {
        if (busy) return;

        setBusy(true);
        setError('');
        setSuccessMessage('');

        try {
            await putSocials({
                id: org_id,
                socials
            }).unwrap();

            dispatch(putSocialsLocal(socials));
            if (!wasReordered) {
                setSuccessMessage(t('organisation:socials:saveSuccess') as string);
            }
            setWasReordered(false);
        } catch (e) {
            const err = e as Hydra.Error;
            setError(err['hydra:description']);
        } finally {
            setBusy(false);
        }
    };

    const openAddModal = () => {
        setNewSocial({ text: '', url: '', type: 'other' });
        setIsAddModalOpen(true);
    };

    const closeAddModal = () => {
        setNewSocial({ text: '', url: '', type: 'other' });
        setIsAddModalOpen(false);
    };

    const addSocial = () => {
        if (FailedRequirements(newSocial, 'url', 'text')) return setError(t("sponsor:error") || "")
        if (socials.length >= 25) return;

        const updatedSocials = [...socials, newSocial];
        setSocials(updatedSocials);

        const updatedCustomButtonText = [...customButtonText, newSocial.text];
        const updatedCustomButtonUrl = [...customButtonUrl, newSocial.url];
        const updatedCustomButtonIcon = [...customButtonIcon, newSocial.type];
        setCustomButtonText(updatedCustomButtonText);
        setCustomButtonUrl(updatedCustomButtonUrl);
        setCustomButtonIcon(updatedCustomButtonIcon);

        closeAddModal();
        setShouldSubmit(true);
    };

    const deleteSocial = async (index: number) => {
        if (!(await modalContext.show(t("organisation:socials:deleteMessage"), t("organisation:socials:deleteMessageText")))) {
            modalContext.hide();
            return;
        }

        modalContext.hide();

        setSocials((prevSocials) => {
            const updatedSocials = prevSocials.filter((_, i) => i !== index);

            setCustomButtonText((prev) => prev.filter((_, i) => i !== index));
            setCustomButtonUrl((prev) => prev.filter((_, i) => i !== index));
            setCustomButtonIcon((prev) => prev.filter((_, i) => i !== index));
            return updatedSocials;
        });

        setShouldSubmit(true);
    };

    const openEditModal = (index: number) => {
        setEditingSocial(socials[index]);
        setEditingIndex(index);
        setIsEditModalOpen(true);
    };

    const closeEditModal = () => {
        setEditingSocial(null);
        setEditingIndex(null);
        setIsEditModalOpen(false);
    };

    const saveEditedSocial = () => {
        if (FailedRequirements(editingSocial, 'url', 'text')) return setError(t("sponsor:error") || "")
        if (editingIndex === null || !editingSocial) return;

        const updatedSocials = [...socials];
        updatedSocials[editingIndex] = editingSocial;
        setSocials(updatedSocials);

        const updatedCustomButtonText = [...customButtonText];
        const updatedCustomButtonUrl = [...customButtonUrl];
        const updatedCustomButtonIcon = [...customButtonIcon];
        updatedCustomButtonText[editingIndex] = editingSocial.text;
        updatedCustomButtonUrl[editingIndex] = editingSocial.url;
        updatedCustomButtonIcon[editingIndex] = editingSocial.type;
        setCustomButtonText(updatedCustomButtonText);
        setCustomButtonUrl(updatedCustomButtonUrl);
        setCustomButtonIcon(updatedCustomButtonIcon);
        closeEditModal();

        setShouldSubmit(true);
    };

    const moveCard = (index: number, direction: 'left' | 'right') => {
        const newIndex = direction === 'left' ? index - 1 : index + 1;

        if (newIndex < 0 || newIndex >= socials.length) return;

        const reorderArray = <T,>(array: T[], from: number, to: number): T[] => {
            const newArray = [...array];
            const [movedItem] = newArray.splice(from, 1);
            newArray.splice(to, 0, movedItem);
            return newArray;
        };

        setSocials(reorderArray(socials, index, newIndex));
        setCustomButtonText(reorderArray(customButtonText, index, newIndex));
        setCustomButtonUrl(reorderArray(customButtonUrl, index, newIndex));
        setCustomButtonIcon(reorderArray(customButtonIcon, index, newIndex));

        setShouldSubmit(true);
        setWasReordered(true);
    };

    const handleDragStart = (index: number) => {
        setDraggedItem(index);
    };

    const handleDragOver = (e: React.DragEvent, overIndex: number) => {
        e.preventDefault();
        if (draggedItem !== null && draggedItem !== overIndex) {
            setDropTargetIndex(overIndex);
        }
    };

    const handleDragLeave = () => {
        setDropTargetIndex(null);
    };

    const handleDrop = (e: React.DragEvent, dropIndex: number) => {
        e.preventDefault();

        if (draggedItem === null || draggedItem === dropIndex) {
            setDraggedItem(null);
            setDropTargetIndex(null);
            return;
        }

        let actualDropIndex = dropIndex;

        // if the dragged item is before the drop index, we need to adjust the drop index so that the card drops at the correct position
        if (draggedItem < dropIndex) {
            actualDropIndex = dropIndex - 1;
        }

        const reorderArray = <T,>(array: T[], from: number, to: number): T[] => {
            const newArray = [...array];
            const [movedItem] = newArray.splice(from, 1);
            newArray.splice(to, 0, movedItem);
            return newArray;
        };

        setSocials(reorderArray(socials, draggedItem, actualDropIndex));
        setCustomButtonText(reorderArray(customButtonText, draggedItem, actualDropIndex));
        setCustomButtonUrl(reorderArray(customButtonUrl, draggedItem, actualDropIndex));
        setCustomButtonIcon(reorderArray(customButtonIcon, draggedItem, actualDropIndex));

        setDraggedItem(null);
        setDropTargetIndex(null);

        setShouldSubmit(true);
        setWasReordered(true);
    };

    const handleDragEnd = () => {
        setDraggedItem(null);
        setDropTargetIndex(null);
    };

    if (!org_socials) return <LoadingPage />

    const FontawsomeAsButton = ({ icon, onClick, className, hidden }: { icon: IconDefinition, onClick: () => void, className: string, hidden?: boolean }) => {
        if (hidden) return null;
        return (
            <FontAwesomeIcon
                icon={icon}
                className={className}
                onClick={onClick}
                onKeyDown={e => e.key === 'Enter' && onClick()}
                tabIndex={0}
            />
        )
    };

    return (
        <>
            <PageMaxWidth pageType='form'>
                <BusyArea busy={busy}>
                    <MobilePreviewWrapper mobileNode={
                        <MobileApp activeItem="socials">
                            <Socials
                                customButton
                                customButtonUrl={customButtonUrl || ''}
                                customButtonText={customButtonText || ''}
                                customButtonIcon={customButtonIcon || ''}
                            />
                        </MobileApp>
                    }>
                        <Title text={t('organisation:socials:title')} subtitle={`${t('organisation:socials:subtitle')} \n \n ${t('organisation:socials:description')}`} />

                        <StatusPopups setText={setError} type='error' text={error} />
                        <StatusPopups setText={setSuccessMessage} type='success' text={successMessage} />

                        <ScrollGradientContainer maxHeight="800px" className="pb-20">
                            <div className="grid grid-cols-2 sm:grid-cols-3 2xl:grid-cols-4 gap-4 justify-items-center">
                                <AnimatePresence>
                                    {socials.map((s, i) => (
                                        <>
                                            {dropTargetIndex === i && (<div
                                                onDragStart={() => handleDragStart(i)}
                                                onDragOver={(e) => handleDragOver(e, i)}
                                                onDragLeave={handleDragLeave}
                                                onDrop={(e) => handleDrop(e, i)}
                                                onDragEnd={handleDragEnd}
                                                className='flex-grow w-full max-h-[150px] min-h-[150px] flex border-2 border-dashed border-gray-300 rounded-[0.4rem]'
                                                key={`drop-target-${i}`}
                                            />)}
                                            <motion.div
                                                key={`social-${i}`}
                                                initial={{ scale: 0.9 }}
                                                animate={{ scale: 1, display: draggedItem === i ? 'none' : 'block' }}
                                                exit={{ scale: 1.1 }}
                                                id={`social${i}`}
                                                className={`flex-grow flex w-full`}
                                                draggable={!isMobile}
                                                onDragStart={() => handleDragStart(i)}
                                                onDragOver={(e) => handleDragOver(e, i)}
                                                onDragLeave={handleDragLeave}
                                                onDrop={(e) => handleDrop(e, i)}
                                                onDragEnd={handleDragEnd}
                                            >
                                                <Card rounded className='relative flex-grow w-full max-h-[150px] min-h-[150px] flex flex-col items-center justify-center gap-1'>
                                                    <div className={`absolute left-1/2 -translate-x-1/2 top-1 cursor-move flex justify-center touch-manipulation ${isMobile ? 'hidden' : ''}`}>
                                                        <FontAwesomeIcon
                                                            icon={faGripLines}
                                                            className='text-gray-400 text-[16px] hover:text-gray-600 transition-colors duration-200'
                                                        />
                                                    </div>

                                                    <FontawsomeAsButton
                                                        icon={faEdit}
                                                        onClick={() => openEditModal(i)}
                                                        className='text-accent-light cursor-pointer hover:scale-110 transition-transform duration-200 absolute top-1 left-1 p-2'
                                                    />

                                                    <FontawsomeAsButton
                                                        onClick={() => deleteSocial(i)}
                                                        icon={faTrashAlt}
                                                        className='text-accent cursor-pointer hover:scale-110 transition-transform duration-200 absolute top-1 right-1 p-2'
                                                    />

                                                    <FontawsomeAsButton
                                                        onClick={() => moveCard(i, 'left')}
                                                        icon={faArrowLeft}
                                                        className={`text-accent cursor-pointer hover:scale-110 transition-all duration-200 absolute bottom-1 left-1 p-2 ${isMobile ? '' : 'pointer-events-none opacity-0 focus:opacity-100'}`}
                                                        hidden={i === 0}
                                                    />

                                                    <FontawsomeAsButton
                                                        onClick={() => moveCard(i, 'right')}
                                                        icon={faArrowRight}
                                                        className={`text-accent cursor-pointer hover:scale-110 transition-all duration-200 absolute bottom-1 right-1 p-2 ${isMobile ? '' : 'pointer-events-none opacity-0 focus:opacity-100'}`}
                                                        hidden={i === socials.length - 1}
                                                    />

                                                    <FontAwesomeIcon
                                                        icon={
                                                            customButtonIcon[i]
                                                                ? optionsFontAwesome[customButtonIcon[i] as keyof typeof optionsFontAwesome]
                                                                : faGlobe
                                                        }
                                                        className='text-[40px] text-gray-500 mt-6'
                                                    />

                                                    <p className="font-bold text-[22px] text-center max-w-full overflow-hidden text-ellipsis line-clamp-1">
                                                        {customButtonText[i] || s.text}
                                                    </p>
                                                </Card>
                                            </motion.div>
                                        </>
                                    ))}

                                    {socials.length < 25 && (
                                        <>
                                            {dropTargetIndex === socials.length && (<div
                                                onDragOver={(e) => handleDragOver(e, socials.length)}
                                                onDragLeave={handleDragLeave}
                                                onDrop={(e) => handleDrop(e, socials.length)}
                                                className='flex-grow w-full max-h-[150px] min-h-[150px] border-2 border-dashed border-gray-300 rounded-[0.4rem]'
                                            />)}
                                            <motion.div
                                                initial={{ opacity: 0, scale: 0.9 }}
                                                animate={{ opacity: 1, scale: 1 }}
                                                exit={{ opacity: 0, scale: 1.1, height: 0 }}
                                                className="flex-grow w-full max-h-[150px] min-h-[150px] flex"
                                                onDragOver={(e) => handleDragOver(e, socials.length)}
                                                onDragLeave={handleDragLeave}
                                                onDrop={(e) => handleDrop(e, socials.length)}
                                            >
                                                <Card rounded className='relative flex-grow w-full flex flex-col items-center justify-center bg-transparent' onClick={openAddModal}>
                                                    <FontAwesomeIcon icon={faPlus} className='text-[40px] text-gray-300' />
                                                </Card>
                                            </motion.div>
                                        </>
                                    )}
                                </AnimatePresence>
                            </div>
                        </ScrollGradientContainer>
                    </MobilePreviewWrapper>
                </BusyArea >
            </PageMaxWidth>

            <GenericModalShell isOpen={isEditModalOpen} onBackdropClick={closeEditModal} className='items-start top-5' modalMarginSmall showOverflow initialFocusRef={inputEditModalRef}>
                {editingSocial && (
                    <Form nostyle submit={saveEditedSocial}>
                        <h2 className="text-3xl font-bold mb-4">{t('organisation:socials:editSocial')}</h2>
                        <Input
                            value={editingSocial.text}
                            label="Name"
                            required
                            invalidator={all(min(1), max(50))}
                            onChange={(e) => setEditingSocial({ ...editingSocial, text: (e.target as HTMLInputElement).value })}
                            ref={inputEditModalRef}
                        />
                        <Input
                            value={editingSocial.url}
                            type="url"
                            placeholder='https://'
                            label="URL"
                            invalidator={min(3)}
                            required
                            onChange={(e) => setEditingSocial({ ...editingSocial, url: (e.target as HTMLInputElement).value })}
                        />
                        <Dropdown
                            value={editingSocial.type}
                            options={Object.fromEntries(option.map(({ value, label }) => [value, label]))}
                            optionsFontAwesome={Object.fromEntries(option.map(({ value, label }) => [value, optionsFontAwesome[value as keyof typeof optionsFontAwesome]]))}
                            label="Type"
                            onChange={(value) => setEditingSocial({ ...editingSocial, type: value as Social['type'] })}
                        />
                        <div className="flex flex-wrap justify-end gap-2 mt-4">
                            <Button nosubmit secondary icon={faXmark} title={t('close')} action={closeEditModal} />
                            <Button icon={faFloppyDisk} title={t('organisation:socials:save')} />
                        </div>
                    </Form>
                )}
            </GenericModalShell>

            <GenericModalShell isOpen={isAddModalOpen} onBackdropClick={closeAddModal} className='items-start top-5' modalMarginSmall showOverflow initialFocusRef={inputAddModalRef}>
                <Form nostyle submit={addSocial}>
                    <h2 className="text-3xl font-bold mb-4">{t('organisation:socials:addSocial')}</h2>
                    <Input
                        value={newSocial.text}
                        label="Name"
                        required
                        invalidator={all(min(1), max(50))}
                        onChange={(e) => setNewSocial({ ...newSocial, text: (e.target as HTMLInputElement).value })}
                        ref={inputAddModalRef}
                    />
                    <Input
                        value={newSocial.url}
                        label="URL"
                        type="url"
                        placeholder='https://'
                        invalidator={min(3)}
                        required

                        onChange={(e) => setNewSocial({ ...newSocial, url: (e.target as HTMLInputElement).value })}
                    />
                    <Dropdown
                        value={newSocial.type}
                        options={Object.fromEntries(option.map(({ value, label }) => [value, label]))}
                        optionsFontAwesome={Object.fromEntries(option.map(({ value, label }) => [value, optionsFontAwesome[value as keyof typeof optionsFontAwesome]]))}
                        label="Type"
                        onChange={(value) => setNewSocial({ ...newSocial, type: value as Social['type'] })}
                    />
                    <div className="flex flex-wrap justify-end gap-2 mt-4">
                        <Button nosubmit secondary icon={faXmark} title={t('cards:edit:cancel')} action={closeAddModal} />
                        <Button icon={faPlus} title={t('organisation:socials:add')} />
                    </div>
                </Form>
            </GenericModalShell>
        </>
    )
}

export default SocialsPage